import React, { useRef, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useFormikContext, getIn } from 'formik';
import {
  FarmScheduleCoverageTypesByState,
  FarmScheduleCoverageType,
  lookupsJson,
  FarmRiskType
} from '@ourbranch/lookups';
import { getDefaultFarmCoverageD, isFarmCoverageDEditable } from '@ourbranch/farm-utils';

import editIcon from 'core/assets/svg/edit.svg';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import { useToast } from 'core/components/toast';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { Tooltip } from 'core/components/tooltip';
import { LabelTooltip } from 'core/components/label-tooltip';
import {
  SwimmingPoolForm,
  InlandMarineForm,
  RelatedPrivateStructuresForm,
  WellPumpForm,
  DwellingUnderConstructionForm,
  EarthquakeCoverageForm,
  EarthquakePalomarCoverageForm
} from '../sub-risks';
import { BarndomininumDwellingForm } from 'common/components/farm/barndominium-dwelling-form';
import { MobileHomeForm } from 'common/components/farm/mobile-home-form';
import { RenovationFieldsForm } from 'common/components/farm/renovation-fields-form';
import {
  CosmeticDamageExclusionSection,
  RoofExclusionSection,
  ExteriorWallSurfacingExclusionSection,
  OtherStructuresExclusionSection,
  StormDamageExclusionSection
} from '../exclusions';
import { Banner } from 'common/components/banner';
import { BrandFormField } from 'common/components/farm/brand-form-field';

import { useStore } from 'core/store';
import { formatCoverageB } from '../../helpers';
import {
  getDwellingConstructionTypeOptions,
  getPropertyTypeOptions,
  getMaskedResidenceUsageOptions,
  getMaskedCoverageBLabel,
  getMaskedCoverageDLabel,
  getMaskedCoverageDTooltip
} from 'common/helpers/farm-helpers';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmOpenSections } from 'common/hooks/farm-open-sections-context';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import useOptions from 'core/hooks/use-options';
import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import { useFarmMigrationUW } from 'common/hooks/use-farm-migration-uw';
import { appConfig } from 'brand-config';
import useSession from 'core/hooks/use-session';
import { CoverageLimitOverride, isCoverageLimitLockedForUser } from 'common/components/coverage-limit-override';
import { getCoverageAHelpText } from './coverage-a-help-text';
import {
  isBarndominium as isBarndominiumBuilding,
  isMobileHome as isMobileHomeBuilding,
  isModularHome as isModularHomeBuilding
} from './property-type-helpers';
import { isBuildingVacant } from './vacancy';

import useStyles from '../risk.styles';

const numFamiliesInDwellingOptions = [
  { id: '1', value: '1' },
  { id: '2', value: '2' },
  { id: '3', value: '3' },
  { id: '4', value: '4' }
];

const basementTypeOptions = lookupsJson.basementType;
const garageTypeOptions = lookupsJson.garageType;
const homeQualityOptions = lookupsJson.homeQuality;
const lossSettlementOptions = lookupsJson.farm.lossSettlement;
const roofLossSettlementOptions = lookupsJson.farm.roofLossSettlement;
const roofShapeOptions = lookupsJson.roofShape;
const roofTypeOptions = lookupsJson.farm.dwellingRoofType;
const windHailDeductibleOptions = lookupsJson.farm.windstormOrHail;
const heatingOptions = lookupsJson.farm.primaryHeatingSourceOptions;
const propertyTypeOptions = lookupsJson.farm.propertyType;

// The dwelling's valuation on the risk card: the ACV calculation when the loss settlement is ACV,
// otherwise the replacement cost estimate; "N/A" until that total exists.
const formatValuationTotal = (item) => {
  const total =
    item.coverages?.lossSettlement === 'ACV'
      ? item.building?.actualCashValueCalculation?.total
      : item.building?.replacementCostEstimate?.total;
  return total ? `$${total.toLocaleString('en-US')}` : 'N/A';
};

const FO1 = ({ item, index, fieldName, removeRisk, setRiskFieldnameToEdit, canRemoveRisk, locationAddress }) => {
  const classes = useStyles();
  const {
    offer: { offer }
  } = useStore();
  // #32207: route-aware product so construction-type + residence-usage restrictions apply on the
  // policy route (the offer store is empty there); `offer` is still read for offer-route rejections.
  const { farmPolicyType } = useFarmPolicyContext();
  const constructionTypeOptions = getDwellingConstructionTypeOptions(FarmRiskType.Fo1, farmPolicyType);
  // Mask-aware: e.g. Home (Z) must not offer Tenant Occupied Rental (#31691)
  const residenceUsageOptions = getMaskedResidenceUsageOptions(
    FarmRiskType.Fo1,
    farmPolicyType,
    locationAddress?.state
  );
  const { inPolicyRoute } = useRouteHelper();
  // #34201: only underwriters servicing a migrated (imported-then-renewed) term may refresh the
  // 360Value (Coverage A) and override Coverage A down to $1,000.
  const { canUwRefresh360Value } = useFarmMigrationUW();
  const { setFieldValue, errors, initialValues, values } = useFormikContext();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const { isCurrentSectionOpen, toggleSection } = useFarmOpenSections(fieldName);
  const { canOverrideCoverageLimit } = useSession();
  // #35617: lock Coverage A for a non-authority user once an authorized user has overridden the limit.
  const coverageLimitOverrideEnabled = Boolean(appConfig?.featureFlags?.coverageLimitOverride);
  const coverageLimitLockedForUser = isCoverageLimitLockedForUser({
    coverages: item.coverages,
    canOverrideCoverageLimit,
    coverageLimitOverrideEnabled
  });
  // #35868: an authority user sees the override rules (not the standard range) while the limit is overridden.
  const showCoverageLimitOverrideRules = Boolean(canOverrideCoverageLimit && coverageLimitOverrideEnabled);
  const debounceTimerRef = useRef(null);
  const toast = useToast();
  const rejections = offer?.rejections || [];
  const address = locationAddress;
  const coverageA = item.coverages?.coverageA;
  const hasBeenRated = !!item.id;

  // Mobile/modular detection accepts both `propertyType` and the
  // legacy `typeOfHome` field via the shared helper, matching the
  // dec mapper's `isMobileOrModularRC`. (#30735 audit alignment.)
  const isBarndominium = isBarndominiumBuilding(item?.building);
  const isMobileHome = isMobileHomeBuilding(item?.building);
  const isModularHome = isModularHomeBuilding(item?.building);

  const needMoreData = rejections.some((rejection) => rejection.code === '50006') || ['FO4', 'FO6'].includes(item.type);
  const state = address?.state;
  // Brand-aware so CFM/MO gets its extra Exterior Wall Finish options (Stone Veneer Natural, Log
  // Siding); every other brand falls back to the base list. #34910
  const exteriorWallTypeOptions = useOptions('exteriorWallType', state);
  const riskType = FarmRiskType.Fo1;
  const availableCoverageTypes = FarmScheduleCoverageTypesByState[state] || [];
  // Get Mine Subsidence coverage limit from appConfig (GitHub issue #28634)
  const mineSubsidenceCoverageLimit = appConfig.farm?.[state]?.coverageLimits?.mineSubsidenceCoverageLimit || 750000;
  // Get mobile home minimum $/sq ft from appConfig, default $7 (GitHub issue #28969)
  const mobileHomeMinPerSqFt =
    appConfig.farm?.[state]?.risks[riskType]?.businessRules?.coverageA?.mobileHome?.minPerSqFt || 7;
  // Coverage D rules drive the percent-of-Coverage-A defaults (GitHub issue #30548).
  // R = 10%, P/S = 20%, V = $0 — read from brand config, no hardcoded percentages.
  const businessRulesD = appConfig.farm?.[state]?.risks?.[riskType]?.businessRules?.coverageD;

  const riskCoverages = getIn(values, fieldName)?.coverages || {};

  const initialCoverageA = getIn(initialValues, `${fieldName}.coverages.coverageA`);
  const initialBuilding = getIn(initialValues, `${fieldName}.building`);
  const initialConnectedHome = getIn(initialValues, `${fieldName}.connectedHome`);
  const initialCanEditCoverageAmount = getIn(initialValues, `${fieldName}.coverages.canEditCoverageAmount`);
  const coverageHasBeenUpdatedRef = useRef(false);

  // Update ref when coverage changes
  useEffect(() => {
    if (hasBeenRated) {
      coverageHasBeenUpdatedRef.current =
        initialCoverageA !== item.coverages?.coverageA &&
        initialCanEditCoverageAmount === item.coverages?.canEditCoverageAmount;
    }
  }, [initialCoverageA, item.coverages?.coverageA]);

  const toastRCEWarning = () => {
    if (coverageHasBeenUpdatedRef.current) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const isSelectedHeatingSourceUnitCountBased =
    heatingOptions.find((option) => option.id === item?.building?.primaryHeatingSource)?.unitCountBased ?? false;

  const propertyTypeLabel = propertyTypeOptions.find((option) => option.id === item?.building?.propertyType)?.value;
  const riskTitle = `FO-1 Basic${item?.building?.propertyType ? `: ${propertyTypeLabel}` : ''}`;

  // Check if residence usage is Vacant to disable Coverage D. Vacancy
  // detection goes through the shared helper so any offer with the
  // FO6Structures-style `residenceType=vacant` shape (#30855) is also
  // recognised — matches the dec mapper's `isBuildingVacant` tolerance
  // for either field. (#30735 audit alignment.)
  const residenceUsage = item?.building?.residenceUsage;
  const isCoverageDDisabled = isBuildingVacant(item?.building);

  // Force Coverage D to $0 when residence usage is Vacant, otherwise default to the
  // brand-config percentage of Coverage A (P/S = 20%, R = 10%) — GitHub issues #29855, #30548.
  // Skip while the form is disabled (read-only) so we never mutate a locked form, matching the
  // FO2/FO3 Coverage D sync added in #31480.
  useEffect(() => {
    if (disableRiskForm) {
      return;
    }
    // Only carriers that LOCK Coverage D at a percent of Coverage A (e.g. Forreston) auto-sync it.
    // CFM's filed MO spec (#31667/#32560) makes it an editable $0–$3,000 field, so honor the
    // brand-config `allowEditing` flag and never stomp the agent-entered value (issue #33060).
    if (isFarmCoverageDEditable({ residenceUsage, businessRulesD })) {
      return;
    }
    if (isCoverageDDisabled && item.coverages?.coverageD !== 0) {
      setFieldValue(`${fieldName}.coverages.coverageD`, 0);
    } else if (['P', 'S', 'R'].includes(residenceUsage) && item.coverages?.coverageA != null) {
      const expectedCoverageD = getDefaultFarmCoverageD({
        residenceUsage,
        coverageA: item.coverages.coverageA,
        businessRulesD
      });
      if (item.coverages?.coverageD !== expectedCoverageD) {
        setFieldValue(`${fieldName}.coverages.coverageD`, expectedCoverageD);
      }
    }
  }, [
    disableRiskForm,
    isCoverageDDisabled,
    residenceUsage,
    item.coverages?.coverageA,
    item.coverages?.coverageD,
    fieldName,
    setFieldValue,
    businessRulesD
  ]);

  // Set default values for ownership and home type for new risks only
  useEffect(() => {
    if (!isBarndominium && !isMobileHome) {
      // Only set defaults if values don't already exist (new risk)
      if (!item?.building?.ownershipStatus) {
        setFieldValue(`${fieldName}.building.ownershipStatus`, 'P');
      }
      if (!item?.building?.typeOfHome) {
        setFieldValue(`${fieldName}.building.typeOfHome`, '1');
      }
    }
  }, [
    fieldName,
    isBarndominium,
    isMobileHome,
    item?.building?.ownershipStatus,
    item?.building?.typeOfHome,
    setFieldValue
  ]);

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">{riskTitle}</Label>
            <div className={classes.divider} />
            <Label type="infoSubtitleSmall">{item.description}</Label>
            <IconButton onClick={() => setRiskFieldnameToEdit(fieldName)} className={classes.editButton}>
              <img className={classes.editIcon} src={editIcon} alt="Edit row" />
            </IconButton>
          </div>
        </div>
      }
      isRemovable={canRemoveRisk}
      onDelete={() => removeRisk(index)}
      disableRemoveButton={disableRiskForm}
      open={isCurrentSectionOpen}
      onChange={toggleSection}
      id={fieldName}
    >
      <Suspense
        fallback={
          <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
            <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
              <CircularProgress className={classes.loader} />
            </Grid>
          </Grid>
        }
      >
        {!hasBeenRated && !isBarndominium && !isMobileHome && (
          <Banner
            variant="notification"
            title={
              inPolicyRoute
                ? "We'll pull data for this dwelling or structure—go ahead and preview changes."
                : "We'll pull data for this dwelling or structure—go ahead and update the offer."
            }
            subtitle="Or enter the details manually if you prefer."
          />
        )}
        {((hasBeenRated && needMoreData) || (!hasBeenRated && (isBarndominium || isMobileHome))) && (
          <Banner
            variant="warning"
            title="Data for this dwelling or structure isn't available—please enter the details manually."
            subtitle={
              inPolicyRoute
                ? "This information won't auto-fill, even if you preview changes."
                : "This information won't auto-fill, even if you update the offer."
            }
          />
        )}
        <Grid container justifyContent="space-between" className={classes.riskCard} spacing={2}>
          {!isBarndominium && !isMobileHome && (
            <>
              {/* Issue #30827: Structure Category is a property characteristic and must be editable
                  wherever Residence Type/Usage is editable. */}
              <Field
                xs={6}
                type="select"
                name={`${fieldName}.building.propertyType`}
                id={`${fieldName}.building.propertyType`}
                mode="light"
                label="Structure Category"
                options={getPropertyTypeOptions(FarmRiskType.Fo1)}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.propertyType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                name={`${fieldName}.building.residenceUsage`}
                id={`${fieldName}.building.residenceUsage`}
                mode="light"
                label="Residence Type/Usage"
                options={residenceUsageOptions}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.residenceUsage) {
                    toastRCEWarning();
                  }
                }}
              />
              <Grid item xs={12}>
                <Label type="infoTitleSmall">Property</Label>
              </Grid>
              <Field
                xs={4}
                name={`${fieldName}.building.yearBuilt`}
                id={`${fieldName}.building.yearBuilt`}
                type="numeric"
                label="Year Built In"
                mode="light"
                format={{
                  allowNegative: false,
                  isAllowed: ({ value }) => value.length <= 4
                }}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.yearBuilt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.sqFt`}
                name={`${fieldName}.building.sqFt`}
                mode="light"
                label="Above-ground sqft"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.sqFt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.basementSqFt`}
                name={`${fieldName}.building.basementSqFt`}
                mode="light"
                label="Basement sqft"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.basementSqFt) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                options={numFamiliesInDwellingOptions}
                id={`${fieldName}.building.numFamiliesInDwelling`}
                name={`${fieldName}.building.numFamiliesInDwelling`}
                mode="light"
                label="Families in Dwelling"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFamiliesInDwelling) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                type="select"
                options={homeQualityOptions}
                id={`${fieldName}.building.homeQuality`}
                name={`${fieldName}.building.homeQuality`}
                mode="light"
                label="Home Quality"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.homeQuality) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numStories`}
                name={`${fieldName}.building.numStories`}
                mode="light"
                label="Stories"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numStories) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numHalfBathrooms`}
                name={`${fieldName}.building.numHalfBathrooms`}
                mode="light"
                label="Half Bathrooms"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numHalfBathrooms) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="numeric"
                id={`${fieldName}.building.numFullBathrooms`}
                name={`${fieldName}.building.numFullBathrooms`}
                mode="light"
                label="Full Bathrooms"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFullBathrooms) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={3}
                type="select"
                options={constructionTypeOptions}
                id={`${fieldName}.building.constructionType`}
                name={`${fieldName}.building.constructionType`}
                mode="light"
                label="Construction Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.constructionType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.numFireplaces`}
                name={`${fieldName}.building.numFireplaces`}
                mode="light"
                label="Fireplaces"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numFireplaces) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.numWoodBurningStoves`}
                name={`${fieldName}.building.numWoodBurningStoves`}
                mode="light"
                label="Wood Stoves"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.numWoodBurningStoves) {
                    toastRCEWarning();
                  }
                }}
              />
              <Grid item xs={4}>
                <LabelTooltip
                  label="Solid Fuel-Burning Appliances"
                  tooltip={{
                    label: 'More Info',
                    onHoverText:
                      'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
                  }}
                  mode="light"
                >
                  <Field
                    type="numeric"
                    id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                    name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                    mode="light"
                    format={{
                      allowNegative: false
                    }}
                    disabled={disableRiskForm}
                    onChange={(val) => {
                      if (val !== initialBuilding?.numSolidFuelBurningAppliances) {
                        toastRCEWarning();
                      }
                    }}
                  />
                </LabelTooltip>
              </Grid>
              <Field
                xs={4}
                type="select"
                options={basementTypeOptions}
                id={`${fieldName}.building.basementType`}
                name={`${fieldName}.building.basementType`}
                mode="light"
                label="Basement/Foundation Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.basementType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="select"
                options={exteriorWallTypeOptions}
                id={`${fieldName}.building.exteriorWallType`}
                name={`${fieldName}.building.exteriorWallType`}
                mode="light"
                label="Exterior Wall Finish"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.exteriorWallType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.garageCarCapacity`}
                name={`${fieldName}.building.garageCarCapacity`}
                mode="light"
                label="Garage's Car Space"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.garageCarCapacity) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="select"
                options={garageTypeOptions}
                id={`${fieldName}.building.garageType`}
                name={`${fieldName}.building.garageType`}
                mode="light"
                label="Garage Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.garageType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                type="numeric"
                id={`${fieldName}.building.roofYear`}
                name={`${fieldName}.building.roofYear`}
                mode="light"
                label="Roof Year"
                format={{
                  allowNegative: false,
                  isAllowed: ({ value }) => value.length <= 4
                }}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofYear) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={4}
                id={`${fieldName}.building.roofType`}
                name={`${fieldName}.building.roofType`}
                type="select"
                options={roofTypeOptions}
                mode="light"
                label="Roof Type"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofType) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.roofShape`}
                name={`${fieldName}.building.roofShape`}
                type="select"
                options={roofShapeOptions}
                mode="light"
                label="Roof Shape"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.roofShape) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.primaryHeatingSource`}
                name={`${fieldName}.building.primaryHeatingSource`}
                type="select"
                options={heatingOptions}
                mode="light"
                label="Primary Heating"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.primaryHeatingSource) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.building.secondaryHeatingSource`}
                name={`${fieldName}.building.secondaryHeatingSource`}
                type="select"
                options={heatingOptions}
                mode="light"
                label="Secondary Heating"
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialBuilding?.secondaryHeatingSource) {
                    toastRCEWarning();
                  }
                }}
              />
              <RenovationFieldsForm
                basePath={fieldName}
                disabled={disableRiskForm}
                onFieldChange={() => toastRCEWarning()}
              />
              <Field
                xs={6}
                id={`${fieldName}.connectedHome.theftPreventionDeviceType`}
                name={`${fieldName}.connectedHome.theftPreventionDeviceType`}
                type="checkbox"
                mode="light"
                label="Theft protective device"
                disabled={disableRiskForm}
                checkedValue="centralStation"
                uncheckedValue="none"
                onChange={(_e, checked) => {
                  const initialIsChecked = initialConnectedHome?.theftPreventionDeviceType === 'centralStation';
                  if (checked !== initialIsChecked) {
                    toastRCEWarning();
                  }
                }}
              />
              <Field
                xs={6}
                id={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
                name={`${fieldName}.connectedHome.fireProtectiveDeviceType`}
                type="checkbox"
                mode="light"
                label="Fire protective device"
                disabled={disableRiskForm}
                checkedValue="centralStation"
                uncheckedValue="none"
                onChange={(_e, checked) => {
                  const initialIsChecked = initialConnectedHome?.fireProtectiveDeviceType === 'centralStation';
                  if (checked !== initialIsChecked) {
                    toastRCEWarning();
                  }
                }}
              />

              <Field
                name={`${fieldName}.connectedHome.internetMonitoring`}
                id={`${fieldName}.connectedHome.internetMonitoring`}
                type="checkbox"
                label="Internet connected monitoring device(s)?"
                mode="light"
                xs={6}
                disabled={disableRiskForm}
                onChange={(val) => {
                  if (val !== initialConnectedHome.internetMonitoring) {
                    toastRCEWarning();
                  }
                }}
              />
            </>
          )}

          {/* Barndominium specific fields */}
          {isBarndominium && (
            <BarndomininumDwellingForm
              basePath={fieldName}
              isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
              disableRiskForm={disableRiskForm}
              initialBuilding={initialBuilding}
              initialConnectedHome={initialConnectedHome}
              toastRCEWarning={toastRCEWarning}
              state={state}
              setFieldValue={setFieldValue}
            />
          )}

          {/* Mobile Home specific fields */}
          {isMobileHome && (
            <MobileHomeForm
              basePath={fieldName}
              isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
              disableRiskForm={disableRiskForm}
              initialBuilding={initialBuilding}
              initialConnectedHome={initialConnectedHome}
              toastRCEWarning={toastRCEWarning}
              state={state}
              riskType={riskType}
              setFieldValue={setFieldValue}
            />
          )}

          {(hasBeenRated || coverageHasBeenUpdatedRef.current) && (
            <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
              <Grid container xs={8} className={classes.rceLabel}>
                {/* Issue #28616: Changed to show only the active valuation (ACV for FO1) instead of both RCE and ACV */}
                <Label>
                  {item.coverages?.lossSettlement === 'ACV'
                    ? 'Our estimated actual cash value based on age is'
                    : 'Our estimated cost to replace this home'}
                </Label>
                <Tooltip
                  label="More info"
                  text={
                    item.coverages?.lossSettlement === 'ACV'
                      ? 'ACV (actual cash value) is calculated using 360Value and accounts for depreciation based on age and condition.'
                      : 'RCE (Replacement Cost Estimate) is calculated using 360Value. This is the cost to replace the dwelling with new materials. The selected loss settlement option determines which value applies to the claim.'
                  }
                />
              </Grid>
              <Grid container item xs={4} spacing={2} justifyContent="flex-end">
                <Grid item className={classes.rceValues}>
                  <Label>{item.coverages?.lossSettlement === 'ACV' ? 'ACV' : 'RCE'}</Label>
                  <Label type="infoValueBold">{formatValuationTotal(item)}</Label>
                </Grid>
              </Grid>
            </NotificationCard>
          )}
        </Grid>

        {(hasBeenRated || coverageHasBeenUpdatedRef.current) && (
          <Grid container spacing={2} className={classes.coverageCard}>
            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle">Base coverages</Label>
            </Grid>
            <Grid item xs={12}>
              <LabelTooltip
                label="Dwelling Limit (A)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'The amount we will pay to rebuild the home is based on the replacement cost estimate value—calculated using current construction, materials and labor costs. If you want added peace of mind, adjust the amount.'
                }}
              >
                <Field
                  id={`${fieldName}.coverages.coverageA`}
                  name={`${fieldName}.coverages.coverageA`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disableRiskForm || coverageLimitLockedForUser}
                  onChange={(value) => {
                    // Clear previous timer
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }

                    // Set new timer - auto-set coverageD to brand-config percentage of CovA
                    // (P/S = 20%, R = 10%) — GitHub issues #29855, #30548.
                    debounceTimerRef.current = setTimeout(() => {
                      const usage = item?.building?.residenceUsage;
                      // Skip the derived-default write for editable configs (CFM) — issue #33060.
                      const isPrimarySeasonalOrRental = ['P', 'S', 'R'].includes(usage);
                      if (
                        isPrimarySeasonalOrRental &&
                        !isFarmCoverageDEditable({ residenceUsage: usage, businessRulesD })
                      ) {
                        setFieldValue(
                          `${fieldName}.coverages.coverageD`,
                          getDefaultFarmCoverageD({
                            residenceUsage: usage,
                            coverageA: value,
                            businessRulesD
                          })
                        );
                      }
                    }, 100);
                  }}
                  description={
                    !getIn(errors, `${fieldName}.coverages.coverageA`)
                      ? getCoverageAHelpText({
                          item,
                          state,
                          riskType,
                          initialCoverageA,
                          showOverrideRules: showCoverageLimitOverrideRules
                        })
                      : undefined
                  }
                />
              </LabelTooltip>
            </Grid>

            <div className={classes.coverageToggleContainer}>
              <Grid container justifyContent="space-between">
                <Field
                  id={`${fieldName}.coverages.canEditCoverageAmount`}
                  name={`${fieldName}.coverages.canEditCoverageAmount`}
                  type="switch"
                  mode="dark"
                  label="Adjust dwelling limit more"
                  tooltipText="You may adjust the dwelling limit to an even higher amount with justification"
                  tooltipLabel="More Info"
                  xs={8}
                  disabled={disableRiskForm || coverageLimitLockedForUser}
                  onChange={(_value, isToggledOn) => {
                    // #35617/#35868: while the Coverage A limit is overridden/locked the adjust toggle's
                    // reset must never fire — the pinned value is authoritative (the toggle itself is
                    // independent of the override and may be on or off).
                    if (item.coverages?.limitOverride) {
                      return;
                    }
                    // When toggling off, reset coverageA to ACV (FO1 always uses ACV)
                    // See: https://github.com/gobranch/branch/issues/28969
                    if (!isToggledOn) {
                      const acv = item.building?.actualCashValueCalculation?.total;
                      const rce = item.building?.replacementCostEstimate?.total;
                      const sqFt = item.building?.sqFt;
                      const propertyType = item.building?.propertyType;

                      if (acv) {
                        let resetValue = acv;
                        // Apply mobile home minimum floor from brandConfig
                        if (propertyType === 'MH' && sqFt) {
                          resetValue = Math.max(resetValue, sqFt * mobileHomeMinPerSqFt);
                        }
                        setFieldValue(`${fieldName}.coverages.coverageA`, resetValue);
                      } else if (rce) {
                        // Fallback to RCE if ACV is not available
                        let fallbackValue = rce;
                        // Apply mobile home minimum floor from brandConfig
                        if (propertyType === 'MH' && sqFt) {
                          fallbackValue = Math.max(fallbackValue, sqFt * mobileHomeMinPerSqFt);
                        }
                        setFieldValue(`${fieldName}.coverages.coverageA`, fallbackValue);
                      }
                    }
                  }}
                />
                <Grid className={classes.linkAndIcon}>
                  <a
                    rel="noreferrer noopener"
                    target="_blank"
                    href="https://www.360-value.com/apps/iv/ui/#/login"
                    className={classes.link}
                  >
                    ISO 360 Login
                    <img src={externalLinkIcon} alt="link to ISO 360 login" />
                  </a>
                </Grid>

                {item.coverages?.canEditCoverageAmount && (
                  <Grid item xs={12} className={classes.adjustCoverageSubContainer}>
                    <Field
                      id={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                      name={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                      type="textarea"
                      mode="dark"
                      label="Description of reason for adjusting dwelling limit (required)"
                    />
                  </Grid>
                )}

                {/* #34201: underwriter-only, migrated terms only. When ON, the next re-rate re-pulls
                    the 360Value to refresh Coverage A, and the UW may set Coverage A as low as $1,000
                    with no upper cap. */}
                {canUwRefresh360Value && (
                  <Field
                    id={`${fieldName}.coverages.uwRefresh360Value`}
                    name={`${fieldName}.coverages.uwRefresh360Value`}
                    type="switch"
                    mode="dark"
                    label="Underwriter: refresh 360Value"
                    tooltipText="Migrated policies keep their carried-over dwelling limit. Turn this on (underwriters only) to re-run 360Value on the next rate and refresh Coverage A, and to override Coverage A as low as $1,000 with no upper cap."
                    tooltipLabel="More Info"
                    xs={8}
                    disabled={disableRiskForm}
                    onChange={(_value, isToggledOn) => {
                      // #34201: enabling the refresh also grants the persisted sub-floor Coverage A
                      // approval, so the UW can immediately lower Coverage A (to as low as $1,000) and
                      // have it survive re-rates + agent binds. Turning it off (pre-rate) revokes it.
                      setFieldValue(`${fieldName}.coverages.coverageAFloorApprovedByUW`, isToggledOn);
                    }}
                  />
                )}

                {/* #35617: authority-gated Coverage A limit override (locks the limit). */}
                <CoverageLimitOverride
                  fieldName={`${fieldName}.coverages`}
                  coverageLabel="Coverage A"
                  coverages={item.coverages}
                  disabled={disableRiskForm}
                />

                {coverageLimitLockedForUser && (
                  <Label type="infoLabel" className={classes.coverageLimitLockWarning}>
                    The Coverage A limit is locked. Please reach out to underwriting to make changes.
                  </Label>
                )}
              </Grid>
            </div>

            <Grid item xs={4}>
              <LabelTooltip
                // #32465: Home (Z) / Rental (Y) show "Related Private Structures (B)" per the forms
                label={getMaskedCoverageBLabel(farmPolicyType, state, 'Other Structures (B)')}
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'Provides coverage for buildings and structures on the insured property that are separate from the main dwelling but within 250 feet, such as detached garages, sheds, and fences. This does not include buildings or structures designed for farming use. To add additional related private structure coverage, schedule them below.'
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageB`}
                  name={`${fieldName}.coverages.coverageB`}
                  type="value"
                  mode="dark"
                  value={formatCoverageB(item.coverages)}
                  disabled={disableRiskForm}
                  fast={false} // need this so react updates the options when coverageA changes
                  riskType={riskType}
                  state={state}
                  classes={classes}
                  coverages={riskCoverages}
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={4}>
              <LabelTooltip
                label="Personal Property (C)"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'If something happens to personal property, like theft or damage due to fire, this covers those items.'
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageC`}
                  name={`${fieldName}.coverages.coverageC`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disableRiskForm}
                  riskType={riskType}
                  state={state}
                />
              </LabelTooltip>
            </Grid>
            <Grid item xs={4}>
              <LabelTooltip
                xs={4}
                // #32458: Rental (Y) shows "Loss of Rent (D)" with a loss-of-rent tooltip
                label={getMaskedCoverageDLabel(farmPolicyType, state, 'Addit. Living Cost (D)')}
                tooltip={{
                  label: 'More Info',
                  onHoverText: getMaskedCoverageDTooltip(
                    farmPolicyType,
                    state,
                    'If the home becomes uninhabitable due to some kind of covered loss, this coverage provides temporary housing up to the amount entered here.'
                  )
                }}
              >
                <BrandFormField
                  id={`${fieldName}.coverages.coverageD`}
                  name={`${fieldName}.coverages.coverageD`}
                  type="numeric"
                  mode="dark"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disableRiskForm={disableRiskForm}
                  fast={true}
                  riskType={riskType}
                  state={state}
                  coverages={riskCoverages}
                  riskFieldName={fieldName}
                />
              </LabelTooltip>
            </Grid>

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Deductibles
              </Label>
            </Grid>
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleAllOther`}
              name={`${fieldName}.coverages.deductibleAllOther`}
              type="select"
              mode="dark"
              label="All peril deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />
            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.deductibleWindHail`}
              name={`${fieldName}.coverages.deductibleWindHail`}
              type="select"
              options={windHailDeductibleOptions}
              mode="dark"
              label="Wind & Hail deductible"
              disabled={disableRiskForm}
              riskType={riskType}
              coverages={riskCoverages}
              state={state}
              coverage={riskCoverages?.coverageA}
              description="Wind and hail deductible must match an All Peril when it's value is greater than 2% of coverage A"
            />

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Optional Coverages
              </Label>
            </Grid>

            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.lossSettlement`}
              name={`${fieldName}.coverages.lossSettlement`}
              type="select"
              options={lossSettlementOptions}
              mode="dark"
              label="Building Loss Settlement"
              disabled
              riskType={riskType}
              state={state}
            />

            <BrandFormField
              xs={6}
              id={`${fieldName}.coverages.roofLossSettlement`}
              name={`${fieldName}.coverages.roofLossSettlement`}
              type="select"
              options={roofLossSettlementOptions}
              mode="dark"
              label="Roof Loss Settlement"
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              coverages={riskCoverages}
              riskFieldName={fieldName}
            />

            <BrandFormField
              name={`${fieldName}.coverages.coverageMS`}
              id={`${fieldName}.coverages.coverageMS`}
              type="select"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              coverages={riskCoverages}
              county={address?.county}
              targetCoverage={coverageA}
              targetCoverageLimit={mineSubsidenceCoverageLimit}
              helperTextLabel="Matches coverage A"
            />
            <BrandFormField
              name={`${fieldName}.coverages.hasActiveCoverageMS`}
              id={`${fieldName}.coverages.hasActiveCoverageMS`}
              type="checkbox"
              label="This dwelling has active mine subsidence or unrepaired mine damage"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            {/* checkboxes */}
            <BrandFormField
              name={`${fieldName}.coverages.repairRebuildRequirement`}
              id={`${fieldName}.coverages.repairRebuildRequirement`}
              type="checkbox"
              label="Repair or rebuilding requirement"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
              riskType={riskType}
              riskFieldName={fieldName}
              state={state}
            />

            <Field
              name={`${fieldName}.coverages.serviceLine`}
              id={`${fieldName}.coverages.serviceLine`}
              type="checkbox"
              label="Service line coverage"
              mode="dark"
              xs={6}
              disabled={disableRiskForm}
            />
            {(isMobileHome || isModularHome) && (
              <BrandFormField
                name={`${fieldName}.coverages.replCostMobileHome`}
                id={`${fieldName}.coverages.replCostMobileHome`}
                type="checkbox"
                label="Replacement cost terms for mobile home"
                mode="dark"
                xs={6}
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
                coverages={riskCoverages}
              />
            )}

            <Grid item xs={12} className={classes.bottomMargin}>
              <Label type="formSubTitle" className={classes.sectionDivider}>
                Exclusions/Restrictions
              </Label>
            </Grid>

            {/* cosmetic damage exclusion */}
            <CosmeticDamageExclusionSection
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            <RoofExclusionSection
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
            />

            <ExteriorWallSurfacingExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

            <OtherStructuresExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

            {inPolicyRoute && (
              <StormDamageExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
            )}

            <SwimmingPoolForm fieldName={fieldName} disabled={disableRiskForm} />
            <EarthquakeCoverageForm
              fieldName={`${fieldName}.coverages`}
              disabled={disableRiskForm}
              riskType={riskType}
              state={state}
              zip={address?.zip}
            />
            <EarthquakePalomarCoverageForm fieldName={fieldName} state={state} />

            {/* inland marine */}
            <InlandMarineForm
              fieldName={`${fieldName}.coverages.scheduleCoverages`}
              disabled={disableRiskForm}
              state={state}
              riskType={FarmRiskType.Fo1}
              coverages={riskCoverages}
            />
            {/* related private structures */}
            {availableCoverageTypes.includes(FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES) && (
              <RelatedPrivateStructuresForm
                fieldName={`${fieldName}.coverages.scheduleCoverages`}
                disabled={disableRiskForm}
                address={locationAddress}
                riskType={riskType}
                coverages={riskCoverages}
              />
            )}

            {/* well pump */}
            <WellPumpForm fieldName={`${fieldName}`} disabled={disableRiskForm} />

            {/* dwelling under construction */}
            <DwellingUnderConstructionForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
          </Grid>
        )}
      </Suspense>
    </CollapsibleCard>
  );
};

FO1.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  canRemoveRisk: PropTypes.bool.isRequired,
  removeRisk: PropTypes.func.isRequired,
  setRiskFieldnameToEdit: PropTypes.func.isRequired,
  locationAddress: PropTypes.object.isRequired
};

export default withDatePicker(FO1);
